import type {
    AreaImport,
    ImportLoading,
    ImportState,
    TrackImport,
    WaypointImport,
} from '@gaia/types';
import turfBbox from '@turf/bbox';
import {useEffect} from 'react';

import type {AreaDispatcher} from './useAreas';
import {useLatest} from './useLatest';
import type {TrackDispatcher} from './useTracks';
import type {WaypointDispatcher} from './useWaypoints';

interface DispatchImportProps {
    importState: ImportState;
    setImportState: SetState<ImportState>;
    waypointDispatcher: WaypointDispatcher;
    trackDispatcher: TrackDispatcher;
    areaDispatcher: AreaDispatcher;
    overlays: OverlaysState;
    onOverlaysChange: SetState<OverlaysState>;
    setSelections: SetState<SelectionState[]>;
}

type PartialFeature =
    | PartialWaypoint
    | PartialTrack
    | GeoJSON.Feature<GeoJSON.Polygon, PartialArea>;

function dispatchImport({
    importState,
    setImportState,
    waypointDispatcher,
    trackDispatcher,
    areaDispatcher,
    overlays,
    onOverlaysChange,
    setSelections,
}: DispatchImportProps & {
    importState: ImportLoading;
}) {
    if (!importState.pendingImport) return;

    const waypoints: PartialWaypoint[] = importState.pendingImport.features.filter(
        (feature: PartialFeature) =>
            feature.geometry.type === 'Point' && importState.importTypes?.includes('waypoints')
    );

    const tracks: PartialTrack[] = importState.pendingImport.features.filter(
        (feature: PartialFeature) =>
            feature.geometry.type === 'LineString' && importState.importTypes?.includes('tracks')
    );

    const areas: PartialArea[] = importState.pendingImport.features.filter(
        (feature: PartialFeature) =>
            feature.geometry.type === 'Polygon' && importState.importTypes?.includes('areas')
    );

    const waypointImports: WaypointImport[] = waypointDispatcher({
        type: 'create',
        data: waypoints,
    }).map((localId) => ({type: 'waypoint', localId}));

    const trackImports: TrackImport[] = trackDispatcher({
        type: 'create',
        data: tracks,
    }).map((localId) => ({type: 'track', localId, lngLat: undefined}));

    const areaImports: AreaImport[] = areaDispatcher({
        type: 'create',
        data: areas,
    }).map((localId) => ({type: 'area', localId, lngLat: undefined}));

    onOverlaysChange({
        ...overlays,
        ...(areaImports.length ? {showAreas: true} : undefined),
        ...(waypointImports.length ? {showWaypoints: true} : undefined),
        ...(trackImports.length ? {showTracks: true} : undefined),
    });

    const imports = [...areaImports, ...waypointImports, ...trackImports];

    setSelections(imports);

    setImportState({
        status: 'selecting',
        eventSource: importState.eventSource,
        bounds: turfBbox(importState.pendingImport) as [number, number, number, number],
        imports,
        fileNames: importState.fileNames,
    });
}

export function useDispatchImportEffect({importState, ...props}: DispatchImportProps) {
    const largeImportConfirmed =
        importState.status === 'loading' && importState.largeImportConfirmed;

    const latest = useLatest({importState, props});

    useEffect(() => {
        if (
            latest.current.importState.status === 'loading' &&
            latest.current.importState.largeImportConfirmed
        ) {
            dispatchImport({importState: latest.current.importState, ...latest.current.props});
        }
    }, [latest, largeImportConfirmed]);
}
